import { useState } from 'react';
import * as api from '../api.js';

/**
 * How a jam begins.
 *
 * This screen exists because it was missing, and its absence made the studio
 * unusable from a cold start: a child could fill in the whole brief, press
 * Build it, and be told to start a jam with no way to start one.
 *
 * It could have been fixed by quietly starting a jam on the first build. That
 * would be worse. A jam spends a fixed slice of the week's studio time, and
 * starting that clock is the child's decision to make, not something to happen
 * to them while they are typing. Showing the theme and the time here also makes
 * the countdown mean something when it appears.
 */
type Props = {
  theme: { title: string; hint: string } | null;
  minutesLeftThisWeek: number;
  credits: number;
  onStarted: () => void;
};

export function StartJam({ theme, minutesLeftThisWeek, credits, onStarted }: Props) {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const noTimeLeft = minutesLeftThisWeek <= 0;

  return (
    <section className="startjam">
      <div className="startjam__badge">⚡</div>
      <h2>Ready to make something?</h2>

      {theme && (
        <div className="startjam__theme">
          <strong>This week: {theme.title}</strong>
          <span>{theme.hint}</span>
        </div>
      )}

      <p className="startjam__meta">
        You have <strong>{minutesLeftThisWeek} minutes</strong> of studio time left this week
        {credits > 0 && <> and <strong>{credits} credits</strong></>}.
      </p>

      {noTimeLeft ? (
        <p className="problem">That's all the studio time for this week. Back on Monday!</p>
      ) : (
        <button
          className="go startjam__go"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            setProblem(null);
            try {
              const result = await api.startSession();
              if (result.ok) onStarted();
              else setProblem(result.childMessage ?? 'Could not start a jam just now.');
            } catch {
              setProblem('Could not reach the studio. Try again in a moment.');
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? 'Starting…' : 'Start the jam ✨'}
        </button>
      )}

      {problem && <p className="problem">{problem}</p>}

      <p className="startjam__note">
        When the clock runs out your game is shipped to the Arcade automatically — so finishing is
        the point, not stopping.
      </p>
    </section>
  );
}
